import React from 'react';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';
import 'react-datepicker/dist/react-datepicker.css';
import { FieldLabel, inputStyles } from './ui';

// Period presets and the custom date range used by the workspace dock.

// Presets first, custom last: the common path comes before the advanced one.
export const PERIOD_ORDER = ['today', 'yesterday', 'week', 'month', 'year', 'custom'];

export const defaultPeriodLocale = {
  dateLocale: 'en-US',
  startLabel: 'Start date',
  endLabel: 'End date',
  startPlaceholder: 'Select start date',
  endPlaceholder: 'Select end date',
  periodTypes: [
    { id: 'custom', label: 'Custom range' },
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'week', label: '7 days' },
    { id: 'month', label: '30 days' },
    { id: 'year', label: '365 days' },
  ],
};

const DAYS_BACK = { week: 7, month: 30, year: 365 };

export function computePeriod(type, custom) {
  const today = new Date();
  let startDate = new Date(today);
  let endDate = new Date(today);

  if (type === 'yesterday') {
    startDate.setDate(startDate.getDate() - 1);
    endDate = new Date(startDate);
  } else if (DAYS_BACK[type]) {
    startDate.setDate(startDate.getDate() - DAYS_BACK[type]);
  } else if (type === 'custom' && custom) {
    startDate = custom.startDate;
    endDate = custom.endDate;
  }

  return { type, startDate, endDate };
}

export const formatShortRange = (period, dateLocale = 'en-US') => {
  if (!period || !period.startDate || !period.endDate) return '';
  const format = (date) => date.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit' });
  return `${format(period.startDate)} – ${format(period.endDate)}`;
};

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 200px));
  gap: 12px;
  padding-top: 8px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DateField = styled.label`
  display: block;
  min-width: 0;

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    display: block;
    width: 100%;
  }

  .react-datepicker__input-container input {
    ${inputStyles}
    height: 40px;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
  }
`;

// The calendars render in a body-level portal with fixed positioning, so they are never
// clipped by the dock's folding panel and stay attached while the dock is pinned.
const pickerProps = {
  portalId: 'fp-datepicker-portal',
  popperProps: { strategy: 'fixed' },
  popperPlacement: 'bottom-start',
  maxDate: new Date(),
  dateFormat: 'dd/MM/yyyy',
};

export function CustomRangeFields({ startDate, endDate, onChange, locale = defaultPeriodLocale }) {
  return (
    <Fields>
      <DateField>
        <FieldLabel>{locale.startLabel}</FieldLabel>
        <DatePicker
          {...pickerProps}
          selected={startDate}
          onChange={(date) => date && onChange({ startDate: date, endDate: date > endDate ? date : endDate })}
          selectsStart
          startDate={startDate}
          endDate={endDate}
          placeholderText={locale.startPlaceholder}
        />
      </DateField>
      <DateField>
        <FieldLabel>{locale.endLabel}</FieldLabel>
        <DatePicker
          {...pickerProps}
          selected={endDate}
          onChange={(date) => date && onChange({ startDate, endDate: date })}
          selectsEnd
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          placeholderText={locale.endPlaceholder}
        />
      </DateField>
    </Fields>
  );
}
